import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { partOrderService, inventoryService, vendorService } from '../../services/api';
import {toast} from "react-toastify";
import * as toastr from "react-toastify";


const PartOrderForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isEditing = !!id;
    const suggestedOrder = location.state?.suggestedOrder;
    const cutSheetData = location.state?.cutSheetData;

    console.log("cutSheetData", cutSheetData);

    // Form state
    const [formData, setFormData] = useState({
                                                 supplierId: '',
                                                 orderNumber: '',
                                                 order_date: new Date(),
                                                 expectedDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 2 weeks from now
                                                 paymentTerms: 'Net 30',
                                                 notes: '',
                                                 status: 'draft',
                                                 items: []
                                             });

    // Reference data
    const [vendors, setVendors] = useState([]);
    const [partTypes, setPartTypes] = useState([]);
    const [inventoryData, setInventoryData] = useState({
                                                           generalInventory: [],
                                                           skuSpecificInventory: []
                                                       });
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showOrderDatePicker, setShowOrderDatePicker] = useState(false);
    const [showExpectedDatePicker, setShowExpectedDatePicker] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredPartTypes, setFilteredPartTypes] = useState([]);
    const [selectedVendorPartTypes, setSelectedVendorPartTypes] = useState([]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // Load vendors, part types, and inventory data
                const [vendorsData, inventoryResponse] = await Promise.all([
                                                                               vendorService.getVendors(),
                                                                               inventoryService.getInventory()
                                                                           ]);

                setVendors(vendorsData);

                // Extract inventory data from response
                const { general_inventory, sku_specific_inventory } = inventoryResponse.data;
                setInventoryData({
                                     generalInventory: general_inventory || [],
                                     skuSpecificInventory: sku_specific_inventory || []
                                 });

                // Create a unified list of part types from both inventory types
                const generalParts = general_inventory.map(item => ({
                    ...item.partType,
                    inventory_id: item.id,
                    isSkuSpecific: false,
                    currentStock: item.current_stock,
                    threshold: item.threshold,
                    onOrder: item.on_order,
                    partType: item.part_type.type,
                    partName: item.part_type.name,
                    vendorId: item.part_type.vendor_id,
                }));

                // For SKU-specific parts, we'll include the product info
                const skuParts = sku_specific_inventory.map(item => ({
                    ...item.partType,
                    inventory_id: item.id,
                    isSkuSpecific: true,
                    productId: item.product_id,
                    productSku: item.product?.sku,
                    productName: item.product?.name,
                    currentStock: item.current_stock,
                    threshold: item.threshold,
                    onOrder: item.on_order,
                    partTypeId: item.part_type_id,
                    partType: item.part_type.type,
                    partName: item.part_type.name,
                    vendorId: item.part_type.vendor_id,
                }));

                console.log("SKU PARTS", skuParts);

                // Combine all part types
                const allPartTypes = [...generalParts, ...skuParts];

                console.log("allPartTypes", allPartTypes);
                setPartTypes(allPartTypes);
                setFilteredPartTypes(allPartTypes);

                // If editing, load the order
                if (isEditing) {
                    const orderData = await partOrderService.getPartOrder(id);

                    // Map the server response items to the expected form items format
                    const mappedItems = orderData.items.map(item => ({
                        inventory_id: item.inventory_id,
                        partId: item.part_type_id, // Using inventory_id as the partId for selection
                        part_type_id: item.part_type_id,
                        quantity_ordered: item.quantity_ordered,
                        unitPrice: parseFloat(item.unit_price) || 0,
                        isSkuSpecific: item.product_id !== null,
                        productId: item.product_id,
                        productSku: item.product?.sku,
                        partType: item.part_type.type
                    }));

                    setFormData({
                                    supplierId: orderData.supplier || '',
                                    orderNumber: orderData.order_number || '',
                                    order_date: new Date(orderData.order_date),
                                    expectedDate: orderData.expected_delivery_date ? new Date(orderData.expected_delivery_date) : new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
                                    paymentTerms: orderData.payment_terms || 'Net 30',
                                    notes: orderData.notes || '',
                                    status: orderData.status || 'draft',
                                    items: mappedItems
                                });
                }
                // If we have a suggested order from the inventory thresholds
                else if (suggestedOrder) {
                    // Convert suggested order format to our form data
                    const orderItems = suggestedOrder.parts_by_vendor
                                       ? Object.values(suggestedOrder.parts_by_vendor).flat().map(part => ({
                            partId: part.part_type_id,
                            quantity: part.quantity_to_order,
                            unitPrice: allPartTypes.find(p => p.id === part.part_type_id)?.vendor_price || 0,
                            isSkuSpecific: false
                        }))
                                       : [];

                    // Set first vendor if available
                    const firstVendorId = suggestedOrder.parts_by_vendor
                                          ? Object.keys(suggestedOrder.parts_by_vendor)[0]
                                          : '';

                    setFormData(prev => ({
                        ...prev,
                        supplierId: firstVendorId,
                        order_date: new Date(),
                        expectedDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
                        notes: 'Auto-generated from inventory thresholds.',
                        items: orderItems
                    }));
                }
                // If we have cut sheet data
                else if (cutSheetData) {
                    // Group items by vendor
                    const vendorGroups = cutSheetData?.by_vendor;

                    // Use the first vendor group for this form
                    if (vendorGroups && Object.keys(vendorGroups).length > 0) {
                        const firstVendorId = Object.keys(vendorGroups)[0];
                        const vendorGroup = vendorGroups[firstVendorId];
                        const vendorData = vendorsData.find(v => v.id === parseInt(firstVendorId));

                        // Create order items from cut sheet data
                        const orderItems = vendorGroup.map(item => ({
                            partId: item.part_type_id,
                            part_type_id: item.part_type_id,
                            quantity_ordered: item.quantity_to_order,
                            unitPrice: allPartTypes.find(p => p.id === item.part_type_id)?.vendor_price || 0,
                            isSkuSpecific: true,
                            productId: item.product_id,
                            partType: item.part_type.type,
                            productSku: item.product_sku,
                            inventory_id: item.inventory_id
                        }));

                        console.log(orderItems);

                        setFormData(prev => ({
                            ...prev,
                            supplierId: vendorData ? vendorData.id : '',
                            order_date: new Date(),
                            expectedDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
                            notes: `Auto-generated from cut sheet on ${format(new Date(), 'MM/dd/yyyy')}. Required for upcoming orders.`,
                            items: orderItems
                        }));
                    }
                }
            } catch (error) {
                console.error("Failed to load data:", error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id, isEditing, suggestedOrder, cutSheetData]);

    useEffect(() => {
        // Filter part types based on search query
        if (searchQuery) {
            const filtered = selectedVendorPartTypes.filter(part =>
                                                                (part.name && part.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
                                                                (part.description && part.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
                                                                (part.vendor_sku && part.vendor_sku.toLowerCase().includes(searchQuery.toLowerCase())) ||
                                                                (part.productSku && part.productSku.toLowerCase().includes(searchQuery.toLowerCase()))
            );
            setFilteredPartTypes(filtered);
        } else {
            setFilteredPartTypes(selectedVendorPartTypes);
        }
    }, [searchQuery, selectedVendorPartTypes]);

    // Filter parts by selected vendor
    useEffect(() => {
        if (formData.supplierId) {
            const vendorPartTypes = partTypes.filter(part =>
                                                         part.vendorId === parseInt(formData.supplierId)
            );
            setSelectedVendorPartTypes(vendorPartTypes);
        } else {
            setSelectedVendorPartTypes(partTypes);
        }
    }, [formData.supplierId, partTypes]);

    const handleAddItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [
                ...prev.items,
                { partId: '', quantity: 1, unitPrice: 0, isSkuSpecific: false, productId: null }
            ]
        }));
    };

    const handleRemoveItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleItemChange = (index, field, value) => {
        setFormData(prev => {
            const newItems = [...prev.items];
            newItems[index] = { ...newItems[index], [field]: value };

            // If selected a part, populate the default price if available
            if (field === 'partId' || field === 'inventory_id') {
                const selectedPart = partTypes.find(p => p.inventory_id === parseInt(value));
                if (selectedPart) {
                    newItems[index].unitPrice = selectedPart.unit_price || 0;
                    newItems[index].isSkuSpecific = selectedPart.isSkuSpecific || false;
                    if (selectedPart.isSkuSpecific) {
                        newItems[index].productId = selectedPart.productId;
                        newItems[index].inventory_id = selectedPart.inventory_id;
                        newItems[index].part_type_id = selectedPart.partTypeId;
                    } else {
                        newItems[index].productId = null;
                        newItems[index].inventory_id = null;
                    }
                }
            }

            return { ...prev, items: newItems };
        });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const calculateTotalValue = () => {
        return formData.items.reduce((sum, item) => {
            const quantity = item.quantity_ordered || 0;
            const price = item.unitPrice || 0;
            return sum + (quantity * price);
        }, 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        console.log(formData);

        try {
            // Validate form
            if (!formData.supplierId) {
                throw new Error('Please select a vendor');
            }

            if (formData.items.length === 0) {
                throw new Error('Please add at least one item to the order');
            }

            for (const item of formData.items) {
                if (!item.partId || item.quantity_ordered <= 0) {
                    throw new Error('Please select a part and enter a valid quantity for all items');
                }
            }

            // Prepare data for submission
            const orderData = {
                ...formData,
                order_date: format(formData.order_date, 'yyyy-MM-dd'),
                expected_delivery_date: format(formData.expectedDate, 'yyyy-MM-dd'),
                total_value: calculateTotalValue(),
                items: formData.items.map(item => ({
                    part_type_id: item.part_type_id,
                    inventory_id: item.inventory_id || null,
                    product_id: item.productId || null,
                    quantity_ordered: item.quantity_ordered,
                    unit_price: item.unitPrice
                }))
            };

            // Submit form
            if (isEditing) {
                await partOrderService.updatePartOrder(id, orderData);
                //alert("Order updated successfully"); // Replace with proper toast notification
                toast.success("Order updated successfully");
            } else {
                await partOrderService.createPartOrder(orderData);
                //alert("Order created successfully"); // Replace with proper toast notification
                toast.success("Order created successfully");
            }

            // Navigate back to part orders list
            navigate('/part-orders');
        } catch (error) {
            console.error("Error saving order:", error);
            //alert(error.message); // Replace with proper toast notification
            toast.error(error.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {isEditing ? 'Edit Part Order' : 'Create New Part Order'}
                    </h2>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="p-6 space-y-6">
                        {/* Order Header */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div className="space-y-2">
                                <label htmlFor="supplierId" className="block text-sm font-medium text-gray-700">
                                    Vendor
                                </label>
                                <select
                                    id="supplierId"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={formData.supplierId}
                                    onChange={(e) => handleInputChange('supplierId', e.target.value)}
                                >
                                    <option value="">Select a vendor</option>
                                    {vendors.map(vendor => (
                                        <option key={vendor.id} value={vendor.id}>
                                            {vendor.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="orderNumber" className="block text-sm font-medium text-gray-700">
                                    Order Number
                                </label>
                                <input
                                    id="orderNumber"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                    value={formData.orderNumber}
                                    onChange={(e) => handleInputChange('orderNumber', e.target.value)}
                                    placeholder={isEditing ? '' : 'Auto-generated or enter PO#'}
                                />
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="paymentTerms" className="block text-sm font-medium text-gray-700">
                                    Payment Terms
                                </label>
                                <select
                                    id="paymentTerms"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={formData.paymentTerms}
                                    onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                                >
                                    <option value="Net 30">Net 30</option>
                                    <option value="Net 45">Net 45</option>
                                    <option value="Net 60">Net 60</option>
                                    <option value="COD">COD</option>
                                    <option value="Prepaid">Prepaid</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Order Date
                                </label>
                                <div className="relative">
                                    <button
                                        type="button"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        onClick={() => setShowOrderDatePicker(!showOrderDatePicker)}
                                    >
                                        <span className="flex items-center">
                                          <svg className="h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                          </svg>
                                            {format(formData.order_date, 'MMMM d, yyyy')}
                                        </span>
                                    </button>
                                    {showOrderDatePicker && (
                                        <div className="absolute z-10 mt-1 bg-white rounded-md shadow-lg p-4 border border-gray-200">
                                            <div className="text-center mb-2">
                                                <input
                                                    type="date"
                                                    className="px-2 py-1 border border-gray-300 rounded"
                                                    value={format(formData.order_date, 'yyyy-MM-dd')}
                                                    onChange={(e) => handleInputChange('order_date', new Date(e.target.value))}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="w-full px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                                                onClick={() => setShowOrderDatePicker(false)}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Expected Delivery
                                </label>
                                <div className="relative">
                                    <button
                                        type="button"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        onClick={() => setShowExpectedDatePicker(!showExpectedDatePicker)}
                                    >
                                        <span className="flex items-center">
                                          <svg className="h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                          </svg>
                                            {format(formData.expectedDate, 'MMMM d, yyyy')}
                                        </span>
                                    </button>
                                    {showExpectedDatePicker && (
                                        <div className="absolute z-10 mt-1 bg-white rounded-md shadow-lg p-4 border border-gray-200">
                                            <div className="text-center mb-2">
                                                <input
                                                    type="date"
                                                    className="px-2 py-1 border border-gray-300 rounded"
                                                    value={format(formData.expectedDate, 'yyyy-MM-dd')}
                                                    onChange={(e) => handleInputChange('expectedDate', new Date(e.target.value))}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="w-full px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                                                onClick={() => setShowExpectedDatePicker(false)}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                                    Status
                                </label>
                                <select
                                    id="status"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={formData.status}
                                    onChange={(e) => handleInputChange('status', e.target.value)}
                                    disabled={!isEditing} // Can't set status on creation
                                >
                                    <option value="draft">Draft</option>
                                    <option value="ordered">Ordered</option>
                                    <option value="partial">Partially Received</option>
                                    <option value="received">Received</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                                Notes
                            </label>
                            <textarea
                                id="notes"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={formData.notes}
                                onChange={(e) => handleInputChange('notes', e.target.value)}
                                placeholder="Add any special instructions or notes here..."
                            />
                        </div>

                        {/* Order Items */}
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-800">Order Items</h3>
                                <button
                                    type="button"
                                    onClick={handleAddItem}
                                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
                                >
                                    <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                    </svg>
                                    Add Item
                                </button>
                            </div>

                            {/* Part Search */}
                            <div className="mb-4">
                                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                                    Search Parts
                                </label>
                                <input
                                    type="text"
                                    id="search"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Search by part number, description, or SKU..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            {formData.items.length === 0 ? (
                                <div className="text-center p-6 border border-dashed border-gray-300 rounded-md text-gray-500">
                                    No items added yet. Click "Add Item" to begin.
                                </div>
                            ) : (
                                 <div className="space-y-4">
                                     {formData.items.map((item, index) => (
                                         <div key={item.productId+item.partType+"receive"+index} className="flex flex-wrap items-end gap-4 p-4 border rounded-md border-gray-300">
                                             <div className="flex-1 min-w-48 space-y-2">
                                                 <label className="block text-sm font-medium text-gray-700">Part</label>
                                                 <select
                                                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                     value={item.inventory_id || ""}
                                                     onChange={(e) => handleItemChange(index, 'partId', e.target.value)}
                                                 >
                                                     <option value="">Select a part</option>

                                                     {filteredPartTypes.length > 0 && (
                                                         <>
                                                             <optgroup label="General Parts">
                                                                 {filteredPartTypes
                                                                     .filter(part => !part.isSkuSpecific)
                                                                     .map(part => (
                                                                         <option key={`general-${part.inventory_id}`} value={part.inventory_id}>
                                                                             {part.partName ? `${part.partName}` : ''}
                                                                             {part.description || ''}
                                                                             {part.vendor_sku ? ` (SKU: ${part.vendor_sku})` : ''}
                                                                             {` | Stock: ${part.currentStock}`}
                                                                         </option>
                                                                     ))
                                                                 }
                                                             </optgroup>

                                                             <optgroup label="Product-Specific Parts">
                                                                 {filteredPartTypes
                                                                     .filter(part => part.isSkuSpecific)
                                                                     .map(part => (
                                                                         <option key={`sku-${part.inventory_id}-${part.productId}-${part.partTypeId}`} value={part.inventory_id}
                                                                                 data-product-id={part.productId}>
                                                                             {part.productSku || 'No SKU'}
                                                                             {part.partName ? ` - ${part.partName}` : ''}
                                                                             {` (Stock: ${part.currentStock}`}
                                                                             {` | Threshold: ${part.threshold})`}
                                                                         </option>
                                                                     ))
                                                                 }
                                                             </optgroup>
                                                         </>
                                                     )}
                                                 </select>
                                                 {item.isSkuSpecific && (
                                                     <div className="text-xs text-blue-600 mt-1">
                                                         Product-specific part
                                                     </div>
                                                 )}
                                             </div>
                                             <div className="w-24 space-y-2">
                                                 <label className="block text-sm font-medium text-gray-700">Quantity</label>
                                                 <input
                                                     type="number"
                                                     min="1"
                                                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                     value={item.quantity_ordered}
                                                     onChange={(e) => {
                                                         const value = e.target.value.trim();
                                                         handleItemChange(
                                                             index,
                                                             'quantity_ordered',
                                                             value === '' ? '' : parseInt(value) || 0
                                                         );
                                                     }}
                                                 />
                                             </div>
                                             <div className="w-32 space-y-2">
                                                 <label className="block text-sm font-medium text-gray-700">Unit Price</label>
                                                 <input
                                                     type="number"
                                                     min="0"
                                                     step="0.01"
                                                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                     value={item.unitPrice}
                                                     onChange={(e) => {
                                                         const value = e.target.value.trim();
                                                         handleItemChange(
                                                             index,
                                                             'unitPrice',
                                                             value === '' ? '' : parseInt(value) || 0
                                                         );
                                                     }}
                                                 />
                                             </div>
                                             <div className="w-32 space-y-2">
                                                 <label className="block text-sm font-medium text-gray-700">Total</label>
                                                 <div className="h-10 px-3 py-2 border rounded-md bg-gray-50 text-gray-700">
                                                     ${(item.quantity_ordered * item.unitPrice).toFixed(2)}
                                                 </div>
                                             </div>
                                             <button
                                                 type="button"
                                                 className="p-2 text-gray-500 hover:text-red-500 focus:outline-none"
                                                 onClick={() => handleRemoveItem(index)}
                                             >
                                                 <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                     <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                 </svg>
                                             </button>
                                         </div>
                                     ))}

                                     <div className="flex justify-end mt-6">
                                         <div className="w-48 p-4 border rounded-md border-gray-300 bg-gray-50">
                                             <div className="flex justify-between items-center">
                                                 <span className="text-gray-700">Total:</span>
                                                 <span className="font-bold text-lg">
                                                    ${calculateTotalValue().toFixed(2)}
                                                </span>
                                             </div>
                                         </div>
                                     </div>
                                 </div>
                             )}
                        </div>
                    </div>

                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
                        <button
                            type="button"
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onClick={() => navigate('/part-orders')}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${submitLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            disabled={submitLoading}
                        >
                            {submitLoading ? 'Saving...' : isEditing ? 'Update Order' : 'Create Order'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PartOrderForm;
